import React, { useEffect, useRef, useState } from 'react';
import { FiZap, FiSave, FiArrowUp, FiArrowDown, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Select from 'react-select';
import services from '../../services';
import AppModal from '../../common/AppModal';
import AppSelect from '../../common/AppSelect';
import { PROPERTY_STATUS_LINK_TYPE } from './PropertyStatusLinkForm';
import { LIST_PROCESS_TYPE } from './ListProcessForm';

export const TICKET_PRIORITY_TYPE = 'ticketPriority';

const WHEN_OPTIONS = [
    { value: 'new', label: 'a new mail is received' },
    { value: 'itemAdded', label: 'an item is added / moved to a list' },
    { value: 'noReplyToCustomer', label: 'there is no reply to a customer' },
    { value: 'noReplyFromCustomer', label: 'there is no reply from a customer' },
    { value: PROPERTY_STATUS_LINK_TYPE, label: 'a property status is set on a ticket' },
    { value: LIST_PROCESS_TYPE, label: 'tickets must follow the lists in order (ticket process)' },
    { value: TICKET_PRIORITY_TYPE, label: 'tickets are ordered by status priority (ticket priority)' },
];

const selectStyle = { height: '38px', fontSize: '13px', minWidth: '200px' };
const labelStyle = { fontSize: '12px', fontWeight: 600, color: '#1a7d3e', width: '60px' };
const iconBtn = {
    width: '28px', height: '28px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', color: '#374151',
};
const multiSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        minWidth: '260px',
        borderRadius: '8px',
        fontSize: '13px',
        borderColor: state.isFocused ? '#1a7d3e' : '#d1fae5',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(26,125,62,0.12)' : 'none',
        '&:hover': { borderColor: '#1a7d3e' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        cursor: 'pointer',
        backgroundColor: state.isSelected ? '#e8f5e9' : state.isFocused ? '#f0fdf4' : 'transparent',
        color: state.isSelected ? '#1a7d3e' : '#374151',
    }),
    multiValue: (base) => ({ ...base, backgroundColor: '#e8f5e9', borderRadius: '6px' }),
    multiValueLabel: (base) => ({ ...base, color: '#1a7d3e', fontSize: '12.5px', fontWeight: 600 }),
    multiValueRemove: (base) => ({ ...base, color: '#ef4444', ':hover': { backgroundColor: '#fecaca', color: '#b91c1c' } }),
    indicatorSeparator: () => ({ display: 'none' }),
};
const warnBox = { fontSize: '12.5px', color: '#b91c1c', background: '#fef2f2', borderRadius: '6px', padding: '6px 10px' };

const fieldLabel = (item) => {
    const cf = item?.customField ?? {};
    const getLabel = (v) => (typeof v === 'object' && v !== null ? v.label ?? v.name ?? v.value ?? '' : v ?? '');
    const value = cf.value;
    const label = Array.isArray(value) ? value.map(getLabel).join(', ') : getLabel(value);
    return label || cf.name || 'Unnamed';
};

const emptyLevel = () => ({ customPropertyId: '', customFieldIds: [] });

// Two ways to set up a ticket priority
const MODE_PER_LIST = 'perList'; // one card per list of the board, each with its own priorities
const MODE_SAME = 'same'; // one set of priorities for the chosen lists (or all other lists)

const parseWhen = (rule) =>
    typeof rule?.when === 'string' ? (() => { try { return JSON.parse(rule.when); } catch (e) { return {}; } })() : rule?.when || {};

// Lists a rule owns: its chosen lists + every list it has its own priorities for
const ruleLists = (rule) => {
    const when = parseWhen(rule);
    const chosen = Array.isArray(when?.boardListIds) ? when.boardListIds : [];
    const perList = (rule?.action || []).filter((a) => a?.action === 'priorityLevel' && a.listId).map((a) => a.listId);
    return [...new Set([...chosen, ...perList].filter(Boolean))];
};

const toLevel = (a) => ({
    customPropertyId: a.customPropertyId || '',
    customFieldIds: Array.isArray(a.customFieldIds) ? a.customFieldIds : [],
});
const byOrder = (a, b) => Number(a.order ?? 0) - Number(b.order ?? 0);
const isFilled = (l) => l.customPropertyId && l.customFieldIds.length;

/** Ordered priority rows (property + statuses, ↑ ↓ ×, + Add priority) — used by both modes */
const LevelsEditor = ({ levels, onChange, propertyOptions, fieldsByProperty, disabled }) => {
    const update = (index, patch) => onChange(levels.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    const move = (index, delta) => {
        const to = index + delta;
        if (to < 0 || to >= levels.length) return;
        const next = [...levels];
        [next[index], next[to]] = [next[to], next[index]];
        onChange(next);
    };
    // A status may only be in one priority of the same list
    const usedElsewhere = (index, propId, fieldId) =>
        levels.some((l, j) => j !== index && l.customPropertyId === propId && l.customFieldIds.includes(fieldId));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {levels.map((level, i) => {
                const fieldOptions = fieldsByProperty[level.customPropertyId] || [];
                return (
                    <div
                        key={i}
                        className="flex items-center gap-3 flex-wrap"
                        style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px', background: '#fff' }}
                    >
                        <span style={{ ...labelStyle, width: '28px' }}>{i + 1}</span>
                        <span className="text-sm text-gray-700" style={{ minWidth: '110px' }}>
                            {i === 0 ? 'On top: tickets with' : 'Then: tickets with'}
                        </span>
                        <AppSelect
                            portal
                            value={level.customPropertyId}
                            onChange={(e) => update(i, { customPropertyId: e.target.value, customFieldIds: [] })}
                            placeholder={disabled ? 'Select a board first' : 'Select property'}
                            disabled={disabled}
                            style={selectStyle}
                            options={propertyOptions}
                        />
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            options={fieldOptions.filter(
                                (o) => level.customFieldIds.includes(o.value) || !usedElsewhere(i, level.customPropertyId, o.value),
                            )}
                            value={fieldOptions.filter((o) => level.customFieldIds.includes(o.value))}
                            onChange={(selected) => update(i, { customFieldIds: (selected || []).map((o) => o.value) })}
                            placeholder={level.customPropertyId ? 'Select statuses' : 'Select a property first'}
                            isDisabled={!level.customPropertyId}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={multiSelectStyles}
                        />
                        <button type="button" title="Move up" style={iconBtn} disabled={i === 0} onClick={() => move(i, -1)}>
                            <FiArrowUp size={13} />
                        </button>
                        <button
                            type="button"
                            title="Move down"
                            style={iconBtn}
                            disabled={i === levels.length - 1}
                            onClick={() => move(i, 1)}
                        >
                            <FiArrowDown size={13} />
                        </button>
                        {levels.length > 1 && (
                            <button
                                type="button"
                                title="Remove priority"
                                onClick={() => onChange(levels.filter((_, j) => j !== i))}
                                style={{ ...iconBtn, color: '#ef4444', background: '#fef2f2', borderColor: '#fecaca' }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                );
            })}
            <div>
                <button
                    type="button"
                    className="automation-add-btn"
                    disabled={disabled}
                    style={{ opacity: disabled ? 0.5 : 1 }}
                    onClick={() => onChange([...levels, emptyLevel()])}
                >
                    <FiPlus size={13} />
                    Add priority {levels.length + 1}
                </button>
            </div>
        </div>
    );
};

/**
 * Create / edit a "ticket priority": which statuses come first in the lists of a board.
 * Stored as a Rule of type 'ticketPriority'. Two ways:
 *  - different order for each list (default): one card per list of the board, each list its own priorities
 *    (priority rows carry their listId);
 *  - same order for the chosen lists (or, with no list chosen, for all lists without a priority of their own).
 * A list belongs to one rule only.
 */
const TicketPriorityForm = ({ show, onHide, onCancel, editId, boardName, onTypeChange, onSaved }) => {
    const [boardId, setBoardId] = useState('');
    const [mode, setMode] = useState(MODE_PER_LIST);
    // "same order" mode
    const [boardListIds, setBoardListIds] = useState([]);
    const [levels, setLevels] = useState([emptyLevel(), emptyLevel()]);
    // "different order per list" mode: { [listId]: { on, levels } }
    const [listLevels, setListLevels] = useState({});
    const [listOptions, setListOptions] = useState([]);
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [fieldsByProperty, setFieldsByProperty] = useState({});
    const requestedProperties = useRef(new Set());
    // other priority rules of this board: lists they own + the "all other lists" one
    const [otherRules, setOtherRules] = useState({ taken: {}, restRule: null });
    const [saving, setSaving] = useState(false);
    // off = order by status only; on = a ticket whose guest sent a new email goes on top until we reply
    const [guestFirst, setGuestFirst] = useState(false);

    // Lists of the board, in board order
    useEffect(() => {
        setListOptions([]);
        if (!boardId) return;
        (async () => {
            try {
                const { data } = await services.board.SELECTED_BOARD(boardId);
                setListOptions(
                    (data || [])
                        .slice()
                        .sort((a, b) => Number(a.sequence) - Number(b.sequence))
                        .map((item) => ({ value: item.id, label: item.coloum })),
                );
            } catch (e) {
                console.error(e);
            }
        })();
    }, [boardId]);

    // Properties of the board
    useEffect(() => {
        setPropertyOptions([]);
        if (!boardId) return;
        (async () => {
            try {
                const res = await services.property.GET_PROPERTY_BY_BOARD_ID(boardId);
                setPropertyOptions((res?.data?.data || []).map((p) => ({ label: p.property?.propertyName, value: p.id })));
            } catch (e) {
                console.error(e);
            }
        })();
    }, [boardId]);

    // Statuses of every property used in a priority (loaded once per property)
    useEffect(() => {
        const used = [...levels, ...Object.values(listLevels).flatMap((l) => l.levels || [])]
            .map((l) => l.customPropertyId)
            .filter(Boolean);
        const missing = [...new Set(used)].filter((id) => !fieldsByProperty[id] && !requestedProperties.current.has(id));
        missing.forEach(async (propId) => {
            requestedProperties.current.add(propId);
            try {
                const res = await services.customfield.GET_CUSTOM_FIELD_BY_TASK_ID(propId);
                const options = (res?.data?.data || []).map((item) => ({
                    label: fieldLabel(item),
                    value: item?.customField?.id ?? item?.id,
                }));
                setFieldsByProperty((prev) => ({ ...prev, [propId]: options }));
            } catch (e) {
                console.error(e);
                requestedProperties.current.delete(propId);
            }
        });
    }, [levels, listLevels, fieldsByProperty]);

    // Other priority rules of this board: which lists they already own
    useEffect(() => {
        setOtherRules({ taken: {}, restRule: null });
        if (!boardId) return;
        (async () => {
            try {
                const rules = await services.automations.GET_ALL_AUTOMATIONS(`type=${TICKET_PRIORITY_TYPE}&boardId=${boardId}`);
                const taken = {};
                let restRule = null;
                (rules || [])
                    .filter((r) => r.id !== editId && r.type === TICKET_PRIORITY_TYPE && r.status !== false)
                    .filter((r) => (r.condition || []).some((c) => c.operand === 'board' && c.value === boardId))
                    .forEach((r) => {
                        const lists = ruleLists(r);
                        if (!lists.length) restRule = r;
                        lists.forEach((id) => { taken[id] = r.name || 'another ticket priority'; });
                    });
                setOtherRules({ taken, restRule });
            } catch (e) {
                console.error(e);
            }
        })();
    }, [boardId, editId]);

    // Edit mode: load the saved rule
    useEffect(() => {
        if (!show) return;
        if (!editId) {
            setBoardId('');
            setMode(MODE_PER_LIST);
            setBoardListIds([]);
            setLevels([emptyLevel(), emptyLevel()]);
            setListLevels({});
            setGuestFirst(false);
            return;
        }
        (async () => {
            try {
                const response = await services.automations.GET_BY_ID_AUTOMATIONS(editId);
                const rule = response?.data?.data;
                if (!rule) return;
                setBoardId(rule.when?.boardId || (rule.condition || []).find((c) => c.operand === 'board')?.value || '');
                setGuestFirst(parseWhen(rule).guestFirst === true);
                const rows = (rule.action || []).filter((a) => a.action === 'priorityLevel');
                if (rows.some((a) => a.listId)) {
                    const byList = {};
                    rows.filter((a) => a.listId).sort(byOrder).forEach((a) => {
                        if (!byList[a.listId]) byList[a.listId] = { on: true, levels: [] };
                        byList[a.listId].levels.push(toLevel(a));
                    });
                    setMode(MODE_PER_LIST);
                    setListLevels(byList);
                    setBoardListIds([]);
                    setLevels([emptyLevel(), emptyLevel()]);
                } else {
                    const saved = rows.sort(byOrder).map(toLevel);
                    setMode(MODE_SAME);
                    setBoardListIds(ruleLists(rule));
                    setLevels(saved.length ? saved : [emptyLevel(), emptyLevel()]);
                    setListLevels({});
                }
            } catch (e) {
                console.error(e);
            }
        })();
    }, [show, editId]);

    const propertyName = (id) => propertyOptions.find((o) => o.value === id)?.label || '';
    const statusNames = (level) =>
        level.customFieldIds
            .map((id) => (fieldsByProperty[level.customPropertyId] || []).find((o) => o.value === id)?.label)
            .filter(Boolean)
            .join(' / ');
    const listName = (id) => listOptions.find((o) => o.value === id)?.label || '';

    // "different order per list" helpers
    const listEntry = (listId) => listLevels[listId] || { on: false, levels: [emptyLevel()] };
    const setListEntry = (listId, patch) =>
        setListLevels((prev) => ({ ...prev, [listId]: { ...listEntry(listId), ...(prev[listId] || {}), ...patch } }));
    const onLists = listOptions.map((o) => o.value).filter((id) => listLevels[id]?.on);

    // "same order" checks
    const takenLists = boardListIds.filter((id) => otherRules.taken[id]);
    const restClash = !boardListIds.length && otherRules.restRule;

    const buildPayload = () => {
        if (mode === MODE_PER_LIST) {
            if (!onLists.length) return toast.error('Switch on at least one list and set its priorities');
            const taken = onLists.find((id) => otherRules.taken[id]);
            if (taken) return toast.error(`"${listName(taken)}" already has its own ticket priority. Switch it off here.`);
            for (const id of onLists) {
                const rows = listLevels[id].levels || [];
                const filled = rows.filter(isFilled);
                if (!filled.length) return toast.error(`Please set at least one priority for "${listName(id)}"`);
                if (filled.length !== rows.length) {
                    return toast.error(`"${listName(id)}": select a property and at least one status for every priority, or remove the empty one`);
                }
            }
            return {
                type: TICKET_PRIORITY_TYPE,
                name: `Ticket priority (${onLists.map(listName).filter(Boolean).join(', ')})`,
                when: { boardId, boardListIds: onLists, mode: MODE_PER_LIST, guestFirst },
                checklist: [],
                condition: [{ operand: 'board', operator: 'equals', value: boardId }],
                action: onLists.flatMap((listId) =>
                    listLevels[listId].levels.map((l, i) => ({
                        action: 'priorityLevel',
                        order: i,
                        boardId,
                        listId,
                        customPropertyId: l.customPropertyId,
                        customFieldIds: l.customFieldIds,
                    })),
                ),
            };
        }

        if (restClash) {
            return toast.error('This board already has a ticket priority for all other lists. Choose the lists for this one.');
        }
        if (takenLists.length) {
            return toast.error(`"${listName(takenLists[0])}" already has its own ticket priority. Remove it from this one.`);
        }
        const filled = levels.filter(isFilled);
        if (!filled.length) return toast.error('Please set at least one priority');
        if (filled.length !== levels.length) {
            return toast.error('Please select a property and at least one status for every priority, or remove the empty one');
        }
        const where = boardListIds.length ? boardListIds.map(listName).filter(Boolean).join(', ') : 'all lists';
        return {
            type: TICKET_PRIORITY_TYPE,
            name: `Ticket priority (${where}): ${filled.map((l) => statusNames(l) || propertyName(l.customPropertyId)).join(' > ')}`,
            when: { boardId, boardListIds, mode: MODE_SAME, guestFirst },
            checklist: [],
            condition: [{ operand: 'board', operator: 'equals', value: boardId }],
            action: filled.map((l, i) => ({
                action: 'priorityLevel',
                order: i,
                boardId,
                customPropertyId: l.customPropertyId,
                customFieldIds: l.customFieldIds,
            })),
        };
    };

    const handleSave = async () => {
        if (!boardId) return toast.error('Please select the board');
        const payload = buildPayload();
        if (!payload || !payload.type) return; // a toast already explained why

        setSaving(true);
        try {
            const response = !editId
                ? await services.automations.CREATE_AUTOMATIONS(payload)
                : await services.automations.UPDATE_AUTOMATIONS(editId, payload);
            if (response) {
                toast.success(editId ? 'Automations Updated Successfully' : 'Automations Added Successfully');
                onSaved && onSaved();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div style={{ display: 'flex', gap: '10px' }}>
            <button
                type="button"
                style={{
                    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#374151',
                    backgroundColor: '#f3f4f6', border: '1.5px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer',
                }}
                onClick={onCancel || onHide}
            >
                Cancel
            </button>
            <button
                type="button"
                disabled={saving}
                style={{
                    padding: '8px 20px', fontSize: '13px', fontWeight: '600', color: '#fff',
                    backgroundColor: '#1a7d3e', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: saving ? 0.7 : 1,
                }}
                onClick={handleSave}
            >
                <FiSave size={13} />
                {editId ? 'Update' : 'Create'}
            </button>
        </div>
    );

    const modeButton = (value, label, hint) => (
        <label
            style={{
                display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px',
                border: `1.5px solid ${mode === value ? '#1a7d3e' : '#e5e7eb'}`, background: mode === value ? '#f0fdf4' : '#fff', flex: 1,
            }}
        >
            <input
                type="radio"
                name="ticketPriorityMode"
                checked={mode === value}
                onChange={() => setMode(value)}
                style={{ marginTop: '3px', accentColor: '#1a7d3e' }}
            />
            <span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>{label}</span>
                <span style={{ display: 'block', fontSize: '12px', color: '#6b7280' }}>{hint}</span>
            </span>
        </label>
    );

    return (
        <AppModal
            show={show}
            onClose={onHide}
            icon={<FiZap size={17} color="#fff" />}
            title={editId ? 'Edit Automation' : 'Create a new automation'}
            subtitle="Order the tickets of each list by status priority"
            maxWidth="960px"
            footer={footer}
        >
            <div className="automation-form" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-700 w-[60px]">When</span>
                    <AppSelect
                        portal
                        value={TICKET_PRIORITY_TYPE}
                        style={{ ...selectStyle, color: '#1f2937', minWidth: '260px' }}
                        onChange={(e) => onTypeChange(e.target.value)}
                        options={WHEN_OPTIONS}
                    />
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <span style={labelStyle}>BOARD</span>
                    <AppSelect
                        portal
                        value={boardId}
                        onChange={(e) => {
                            setBoardId(e.target.value);
                            setBoardListIds([]);
                            setLevels([emptyLevel(), emptyLevel()]);
                            setListLevels({});
                        }}
                        placeholder="Select board"
                        style={selectStyle}
                        options={boardName || []}
                    />
                </div>

                <div className="flex items-stretch gap-3 flex-wrap" data-testid="ticket-priority-mode">
                    <span style={{ ...labelStyle, paddingTop: '10px' }}>HOW</span>
                    {modeButton(MODE_PER_LIST, 'Different order for each list', 'Set the priorities of every list separately')}
                    {modeButton(MODE_SAME, 'Same order for chosen lists', 'One set of priorities shared by the lists you pick')}
                </div>

                {mode === MODE_PER_LIST && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }} data-testid="ticket-priority-lists">
                        {!boardId && <div className="text-sm text-gray-500">Select a board to see its lists.</div>}
                        {boardId && !listOptions.length && <div className="text-sm text-gray-500">Loading the lists of this board…</div>}
                        {listOptions.map((list) => {
                            const entry = listEntry(list.value);
                            const takenBy = otherRules.taken[list.value];
                            const blocked = !!takenBy && !entry.on;
                            return (
                                <div
                                    key={list.value}
                                    data-testid={`priority-list-${list.label}`}
                                    style={{
                                        border: `1.5px solid ${entry.on ? '#1a7d3e' : '#e5e7eb'}`, borderRadius: '10px', padding: '10px 12px',
                                        background: blocked ? '#f9fafb' : entry.on ? '#f8fdf9' : '#fff', opacity: blocked ? 0.65 : 1,
                                    }}
                                >
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937', flex: 1 }}>{list.label}</span>
                                        {blocked ? (
                                            <span style={{ fontSize: '12px', color: '#6b7280' }}>Has its own priority ({takenBy})</span>
                                        ) : (
                                            <label className="flex items-center gap-2" style={{ cursor: 'pointer', fontSize: '12.5px', color: '#374151' }}>
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Order ${list.label} by priority`}
                                                    checked={!!entry.on}
                                                    onChange={(e) => setListEntry(list.value, { on: e.target.checked })}
                                                    style={{ accentColor: '#1a7d3e', width: '16px', height: '16px' }}
                                                />
                                                {entry.on ? 'On' : 'Off (normal order)'}
                                            </label>
                                        )}
                                    </div>
                                    {entry.on && (
                                        <div style={{ marginTop: '10px' }}>
                                            {takenBy && (
                                                <div style={{ ...warnBox, marginBottom: '8px' }}>
                                                    This list already has its own ticket priority ({takenBy}). Switch it off here.
                                                </div>
                                            )}
                                            <LevelsEditor
                                                levels={entry.levels}
                                                onChange={(next) => setListEntry(list.value, { levels: next })}
                                                propertyOptions={propertyOptions}
                                                fieldsByProperty={fieldsByProperty}
                                                disabled={!boardId}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {mode === MODE_SAME && (
                    <>
                        <div className="flex items-center gap-3 flex-wrap">
                            <span style={labelStyle}>LISTS</span>
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                options={listOptions.map((o) => ({
                                    ...o,
                                    label: otherRules.taken[o.value] && !boardListIds.includes(o.value)
                                        ? `${o.label} (has its own priority)`
                                        : o.label,
                                }))}
                                isOptionDisabled={(o) => !!otherRules.taken[o.value] && !boardListIds.includes(o.value)}
                                value={boardListIds
                                    .map((id) => listOptions.find((o) => o.value === id))
                                    .filter(Boolean)}
                                onChange={(selected) => setBoardListIds((selected || []).map((o) => o.value))}
                                placeholder={boardId ? 'All lists without their own priority' : 'Select a board first'}
                                isDisabled={!boardId}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={multiSelectStyles}
                            />
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '-6px' }}>
                            These priorities apply to every list picked here. For different priorities per list, choose
                            "Different order for each list".
                        </div>
                        {restClash && (
                            <div style={warnBox}>
                                This board already has a ticket priority for all other lists ({otherRules.restRule.name}). Choose the
                                lists this one is for.
                            </div>
                        )}
                        {takenLists.length > 0 && (
                            <div style={warnBox}>
                                {takenLists.map(listName).join(', ')} already {takenLists.length === 1 ? 'has' : 'have'} its own ticket
                                priority ({otherRules.taken[takenLists[0]]}).
                            </div>
                        )}
                        <LevelsEditor
                            levels={levels}
                            onChange={setLevels}
                            propertyOptions={propertyOptions}
                            fieldsByProperty={fieldsByProperty}
                            disabled={!boardId}
                        />
                    </>
                )}

                <label
                    className="flex items-center gap-2"
                    style={{ cursor: 'pointer', fontSize: '13px', color: '#374151' }}
                    data-testid="ticket-priority-guest-first"
                >
                    <input
                        type="checkbox"
                        checked={guestFirst}
                        onChange={(e) => setGuestFirst(e.target.checked)}
                        style={{ accentColor: '#1a7d3e', width: '16px', height: '16px' }}
                    />
                    A ticket with a new guest email goes to the top until we reply
                </label>

                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.6 }}>
                    <div>
                        In a list, tickets with priority 1 come first, then priority 2, and so on. Tickets without any of these
                        statuses come after all of them. Lists without any ticket priority keep their normal order.
                    </div>
                    <div>
                        {guestFirst
                            ? 'A ticket where the guest sent a new email always comes to the top, whatever its status. After we reply, it goes back to its priority place.'
                            : 'Tickets are ordered by their status only. New emails do not change the order.'}
                    </div>
                    <div>Tickets can still be dragged up and down inside their own priority group.</div>
                </div>
            </div>
        </AppModal>
    );
};

export default TicketPriorityForm;
